import React, { useState } from "react";
import { Sparkles, Send, Loader2, MessageCircleQuestion } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { toast } from "../../lib/utils";
import { getApiKey } from "../../lib/ai";

/**
 * AI Layer — natural language querying across project state.
 * Uses the Anthropic API key already stored client-side (same pattern as the
 * S/4HANA Executive Brief). No server round-trip; the browser calls
 * api.anthropic.com directly with the user-provided key.
 */

const SUGGESTED = [
  "What's blocking UAT sign-off?",
  "Show me the top 5 risks by severity.",
  "Which cutover tasks are behind schedule?",
  "Summarize the value realization state.",
  "Which WRICEF items are still in backlog?",
  "Data migration objects with error rate over 3%.",
];

async function askClaude(question, doc) {
  const key = getApiKey();
  if (!key) throw new Error("Add your Anthropic API key in Settings first");

  // Build a compressed digest so we don't blow the token budget.
  const digest = {
    meta: doc.meta,
    tasksSummary: (doc.tasks || []).map(t => ({ name: t.name, pct: t.pct, critical: t.critical, start: t.start, end: t.end, status: t.status })),
    raids: (doc.raids || []).map(r => ({ ref: r.ref, type: r.type, title: r.title, severity: r.score, owner: r.owner, status: r.status, due: r.due })),
    actions: (doc.actions || []).map(a => ({ id: a.id, title: a.title, owner: a.assignee, status: a.status, due: a.due, priority: a.priority })),
    testing: {
      cycles: (doc.testing?.cycles || []).map(c => ({ name: c.name, type: c.type, executed: c.executed, passed: c.passed, status: c.status })),
      defectsOpen: (doc.testing?.defects || []).filter(d => !["Closed","Deferred"].includes(d.status)).map(d => ({ ref: d.ref, title: d.title, severity: d.severity, status: d.status, cycle: d.cycle })),
    },
    dataMigration: doc.dataMigration || {},
    cutover: {
      runbook: (doc.cutover?.runbook || []).map(r => ({ seq: r.seq, task: r.task, status: r.status, owner: r.owner, startPlan: r.startPlan })),
      hypercareOpen: (doc.cutover?.hypercare || []).filter(h => !["Closed","Resolved"].includes(h.status)),
    },
    fitToStandard: {
      processCounts: (doc.fitToStandard?.processes || []).reduce((acc, p) => { acc[p.fit || "Fit"] = (acc[p.fit || "Fit"] || 0) + 1; return acc; }, {}),
      wricef: (doc.fitToStandard?.wricef || []).map(w => ({ ref: w.ref, type: w.type, name: w.name, status: w.status })),
    },
    valueRealization: doc.valueRealization || {},
    governance: doc.governance || {},
  };

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": key,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: "claude-3-5-sonnet-latest",
      max_tokens: 900,
      messages: [{
        role: "user",
        content: `You are Harmonizer, an AI assistant embedded in an SAP project delivery platform. Answer the user's question based *only* on the provided project state — be direct, cite specific IDs / titles / owners / dates when possible, and highlight anomalies. Use short bullets. If the data doesn't contain the answer, say so.\n\nProject state:\n\`\`\`json\n${JSON.stringify(digest)}\n\`\`\`\n\nQuestion: ${question}`,
      }],
    }),
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(errBody || `HTTP ${res.status}`);
  }
  const j = await res.json();
  return j.content?.[0]?.text || "(no response)";
}

export default function AILayer() {
  const { doc } = useProject();
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const ask = async (question) => {
    if (!question.trim()) return;
    const userMsg = { role: "user", text: question, at: Date.now() };
    setMessages(m => [...m, userMsg]);
    setQ("");
    setLoading(true);
    try {
      const text = await askClaude(question, doc);
      setMessages(m => [...m, { role: "assistant", text, at: Date.now() }]);
    } catch (e) {
      const errText = e?.message || "Unknown error";
      setMessages(m => [...m, { role: "assistant", text: `Error: ${errText}`, at: Date.now(), error: true }]);
      toast(errText.startsWith("Add your Anthropic") ? errText : "Query failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">AI-Assisted Layer</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">AI Layer</h2>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">Natural-language querying across your live project state — no dashboards, no filters. Ask a question, get a targeted answer.</p>
      </div>

      {/* Suggestions */}
      {messages.length === 0 && (
        <div className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Try one of these</div>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED.map(s => (
              <button key={s} className="pill pill-neutral hover:border-zinc-900 transition-colors text-xs" onClick={() => ask(s)} data-testid={`ai-suggest-${s.slice(0,10)}`}>
                <MessageCircleQuestion size={11} className="mr-1"/>{s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Conversation */}
      <div className="card p-4 mb-4 min-h-[280px] max-h-[520px] overflow-y-auto space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-zinc-400 italic text-sm py-8">Ask a question about this project — cutover status, blockers, risks, WRICEF backlog, adoption metrics, budget variance…</div>
        ) : messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-sm text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "bg-zinc-900 text-white" :
              m.error ? "bg-red-50 text-red-900 border border-red-200" : "bg-zinc-50 text-zinc-800 border border-[var(--border)]"
            }`} data-testid={`ai-msg-${i}`}>
              {m.role === "assistant" && !m.error && <div className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-400 mb-1"><Sparkles size={9}/> Harmonizer</div>}
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-zinc-500 italic"><Loader2 size={12} className="animate-spin"/> Thinking…</div>
        )}
      </div>

      {/* Prompt */}
      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="flex gap-2">
        <input
          className="fld flex-1"
          placeholder="Ask anything — 'what's blocking cutover?' 'top 5 open risks by aging'…"
          value={q}
          onChange={e => setQ(e.target.value)}
          data-testid="ai-input"
        />
        <button type="submit" className="btn btn-primary" disabled={loading || !q.trim()} data-testid="ai-submit">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <><Send size={14}/> Ask</>}
        </button>
      </form>

      <div className="font-mono text-[10px] text-zinc-400 mt-3">Uses your Anthropic key stored locally in Settings. Queries execute browser-side — no project data touches Harmonizer servers.</div>
    </>
  );
}
